

import { ArrowUpIcon, LoaderCircle, Paperclip, Plus, Square, SquareCheck, MessageCircle, X, Check, CheckCheck } from "lucide-react"

import { cn } from "@univdiam/ui/lib/utils"
import { Button } from "../../components/button"
import { Input } from "../../components/input"
import { ScrollArea } from "@univdiam/ui/components/scroll-area"
import SectionHeader from "./SectionHeader"
import type { MessageType, Admin, Customer, MediaType } from "@univdiam/ui/type"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { groupMessagesByDateAndSender } from "../Chat/GroupedChat"
import { format, parseISO } from "date-fns"
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar"
import MediaRenderer from "../MediaUploader/MediaRender"
import { formatTimeInUserTimezone } from "@univdiam/ui/lib/formatedDates"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@univdiam/ui/components/tooltip"
import RichTextEditor from "../RichTextEditor"
import { renderRichText, renderPlainText, stripTrailingEmptyBlocks, richTextClass } from "@univdiam/ui/lib/renderRichText"
import { newClientMessageId } from "@univdiam/ui/lib/chatMessages"

const isRichTextEmpty = (html: string) => {
  if (!html) return true
  const clean = html.replace(/<[^>]*>/g, '').trim()
  return clean.length === 0 || html === '<p></p>' || html === '<p><br></p>'
}

const HTML_TAG = /<\/?[a-z][^>]*>/i

/**
 * What a failed send actually means, per acknowledgement code.
 *
 * These used to all render as a bare "Not sent". A message that cannot be
 * delivered because the connection is gone and one the server actively refused
 * need completely different things from the user -- wait, versus stop pressing
 * Retry -- and collapsing them into one line is what made Retry look broken:
 * it was faithfully re-sending into a socket that could never deliver.
 */
const SEND_FAILURE_REASON: Record<string, string> = {
  DISCONNECTED: 'You appear to be offline. Reconnecting…',
  TIMEOUT: 'The server did not respond.',
  NO_RESPONSE: 'The server did not respond.',
  NOT_AUTHORIZED: 'You do not have access to this order.',
  SEND_FAILED: 'The server could not store this message.',
  CLIENT_MSG_ID_CONFLICT: 'A message ID clash. Retrying will resolve it.',
}

const failureReason = (code?: string): string =>
  (code && SEND_FAILURE_REASON[code]) || 'Not sent.'

function formatMessageHTML(message: string): string {
  if (!message) return ''
  // Rich-text messages get sanitized; plain text gets escaped + newlines preserved.
  return HTML_TAG.test(message) ? renderRichText(message) : renderPlainText(message)
}

interface ChatSectionProps {
  onBack?: () => void;
  messages: MessageType[];
  onSendMessage?: (payload: MessageType) => void;
  /** Re-send a message whose delivery failed. Receives the original payload. */
  onRetryMessage?: (payload: MessageType) => void;
  /**
   * The chat connection is down. Sending is deliberately left enabled: a send
   * made while offline becomes a failed bubble with a Retry, which keeps the
   * text safe. The banner exists so a failure that follows is expected rather
   * than mysterious.
   */
  isOffline?: boolean;
  orderId?: string;
  user: Admin | Customer | null;
  orderRef: string;
  handleSendToUpload: (files: File[]) => Promise<string[]>,
  isMediaUploading: boolean,
  isAdmin?: boolean,
  handleOpenAttentionModal?: () => void,
  hasNextPage?: boolean,
  fetchNextPage?: () => void,
  isFetchingNextPage?: boolean,
  isLoading?: boolean,
  headerActions?: React.ReactNode,
  /**
   * The reader's watermark AS OF OPENING this thread -- the id of the last
   * message they had already seen. A "N new messages" divider is drawn above
   * the first message newer than it that came from the other side.
   *
   * Captured once per open (the mark-read response returns it from before the
   * mark), so the line stays where it is while the thread is open: messages
   * arriving live land below it.
   *
   * null/undefined means NOT KNOWN YET -- the mark-read response has not come
   * back -- and draws nothing. 0 is a real answer meaning this reader has read
   * nothing in this thread, so every message from the other side is new and the
   * divider sits at the very top. The two must stay distinguishable: while the
   * request is in flight the state is null, and treating that as 0 flashes a
   * divider above the whole history on every open.
   */
  unreadFromId?: number | null
}


const getUserId = (user: Admin | Customer | null): string | undefined => {
  if (!user) return undefined;

  // Check if user is Admin (has 'id' property)
  if ('id' in user) {
    return user.id;
  }

  // Check if user is Customer (has 'customer_id' property)
  if ('customer_id' in user) {
    return user.customer_id;
  }

  return undefined;
};


export function ChatSection({
  messages,
  onSendMessage,
  onRetryMessage,
  isOffline,
  orderId,
  user,
  orderRef,
  handleSendToUpload,
  isMediaUploading,
  isAdmin,
  handleOpenAttentionModal,
  onBack,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  headerActions,
  unreadFromId
}: ChatSectionProps) {

  const [isAttention, setIsAttention] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [pendingFiles, setPendingFiles] = useState<{ file: File, url: string, name: string, size: number, type: string }[]>([])

  const [input, setInput] = useState("")
  const inputLength = isRichTextEmpty(input) ? 0 : input.trim().length

  /**
   * A composer-level failure -- one that happened before the message reached
   * the network, so there is no bubble to hang a "Not sent" on. An upload that
   * failed used to leave no trace here at all.
   */
  const [composerError, setComposerError] = useState<string | null>(null)

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const previousScrollHeightRef = useRef<number | null>(null);



  // Enter calls sendMessage directly, bypassing the Send button's disabled
  // state, so a second Enter during an upload would send the message twice.
  const sendingRef = useRef(false)

  const sendMessage = async () => {
    if (sendingRef.current) return;
    if (isRichTextEmpty(input) && pendingFiles.length === 0) return;
    sendingRef.current = true
    try {
      await sendMessageNow()
    } finally {
      sendingRef.current = false
    }
  }

  const sendMessageNow = async () => {
    setComposerError(null)

    let uploadedMedia: MediaType[] = [];
    if (pendingFiles.length > 0) {
      const filesToUpload = pendingFiles.map(pf => pf.file);
      const uploadedUrls = await handleSendToUpload(filesToUpload);

      // A failed upload returns fewer URLs than files -- an empty array when it
      // failed outright. Sending anyway used to store a message with the
      // attachments quietly missing, and an attachment-only message became an
      // empty row that looked like nothing had happened at all. Worse, the
      // index-paired mapping below silently mis-labels every attachment once
      // the two lists are different lengths. Stop here instead, and keep the
      // files and the text so the user can send again.
      if (uploadedUrls.length !== filesToUpload.length) {
        setComposerError(
          uploadedUrls.length === 0
            ? 'Attachments could not be uploaded. Nothing was sent — try again.'
            : `Only ${uploadedUrls.length} of ${filesToUpload.length} attachments uploaded. Nothing was sent — try again.`
        )
        return;
      }

      uploadedMedia = uploadedUrls.map((url, index) => ({
        url: url,
        name: pendingFiles[index]?.name || 'Unknown',
        size: pendingFiles[index]?.size || 0,
        type: pendingFiles[index]?.type || ''
      }));
    }

    const newMessage: MessageType = {
      orderRef,
      message: stripTrailingEmptyBlocks(input),
      orderId: orderId!,
      sender_role: user?.role!,
      created_at: new Date().toISOString(),
      sender_id: (user?.role === 'CUSTOMER' || user?.role === 'STAFF')
        ? ('customer_id' in user ? user.customer_id : user.id)!
        : ('id' in user! ? user.id : user?.customer_id)!,
      sender_name: user?.full_name!,
      sender_picture: user?.profile_pic!,
      media_url: uploadedMedia,
      isAttention,
      // Identity, stamped before the message touches the network. Everything
      // downstream -- the optimistic copy, the socket echo, the acknowledgement
      // and any paginated re-fetch -- is reconciled against this, so the same
      // message can never render twice or be lost for lack of a way to name it.
      client_msg_id: newClientMessageId()
    }

    // The parent owns delivery: it holds the optimistic copy until the server
    // acknowledges, then replaces it with the stored row.
    if (onSendMessage) {
      onSendMessage(newMessage)
    }

    setPendingFiles([])
    setInput("")
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await sendMessage()
  }



  const groupedMessages = groupMessagesByDateAndSender(messages);

  /**
   * Divider position and label. `messages` is oldest-first, so the first match
   * is the oldest unread one. Only the other side's messages count -- by ROLE,
   * not sender id: the backend's unread_count uses sender_role, so filtering on
   * `sender_id !== mine` here counted a colleague's message (another ADMIN, or
   * a STAFF under the same customer) that the badge never counted.
   *
   * The count is over what is loaded. A page is 50 messages, so it is exact
   * unless more unread ones sit in a page that has not been fetched -- hence
   * the "50+" form while hasNextPage is still true.
   */
  const { firstUnreadId, unreadLabel } = useMemo(() => {
    if (unreadFromId === null || unreadFromId === undefined) return { firstUnreadId: null, unreadLabel: '' }
    const viewerId = getUserId(user)
    // This is a group chat, not a two-sided conversation: a teammate's message
    // on the reader's own side (another admin, another staff member) is just
    // as unread as one from the opposite side. Only the reader's OWN messages
    // must never count as "new" to themselves.
    const unread = messages.filter(
      (m) => String(m.sender_id) !== String(viewerId) && Number(m.id) > unreadFromId
    )
    if (!unread.length) return { firstUnreadId: null, unreadLabel: '' }
    const truncated = unread.length >= 50 && hasNextPage
    return {
      firstUnreadId: unread[0]!.id ?? null,
      unreadLabel: `${unread.length}${truncated ? '+' : ''} new message${unread.length === 1 ? '' : 's'}`,
    }
  }, [messages, unreadFromId, user, hasNextPage]);

  const handleFiles = (selectedFiles: FileList | File[] | null) => {
    if (!selectedFiles) return;

    const newFiles = Array.from(selectedFiles);

    const filePreviews = newFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop()!
    }));

    setPendingFiles(prev => [...prev, ...filePreviews]);
  };

  const removePendingFile = (indexToRemove: number) => {
    setPendingFiles(prev => {
      const updated = [...prev];
      if (updated[indexToRemove]) {
        URL.revokeObjectURL(updated[indexToRemove].url); // clean up memory
      }
      updated.splice(indexToRemove, 1);
      return updated;
    });
  };


  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (viewport) {
        if (previousScrollHeightRef.current !== null) {
          const currentScrollHeight = viewport.scrollHeight;
          viewport.scrollTop = currentScrollHeight - previousScrollHeightRef.current;
          previousScrollHeightRef.current = null;
        } else {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }
    }
  }, [messages, isMediaUploading])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop === 0 && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      previousScrollHeightRef.current = e.currentTarget.scrollHeight;
      fetchNextPage();
    }
  };
  // console.log('groupedMessages', groupedMessages)

  const handleAttentionToggle = () => {
    setIsAttention(!isAttention)
  }

  return (
    <div className="lg:h-full h-full flex flex-col bg-white border border-[#E9EAEB] rounded-md text-caption1 overflow-hidden">
      <SectionHeader
        title="Share an Update"
        subtitle={orderId ? `#${orderId}` : undefined}
        sectionIcon={<MessageCircle strokeWidth={1.5} className="w-5 h-5 text-gray-600" />}
        isAdmin={isAdmin}
        buttonText={'Attentions'}
        onButtonClick={handleOpenAttentionModal && handleOpenAttentionModal}
        onBack={onBack}
        headerActions={headerActions}
      />

      {isOffline && (
        <div className="shrink-0 px-4 py-1.5 text-xs text-amber-800 bg-amber-50 border-b border-amber-200">
          Not connected. Reconnecting — new messages may not arrive until this clears.
        </div>
      )}

      <div className="flex-1 min-h-0 px-0 pb-2 pt-1 overflow-hidden">
        <ScrollArea ref={scrollAreaRef} className="h-full px-4" onScrollCapture={handleScroll}>
          <div className="flex flex-col gap-4 pr-1 h-full">
            {isFetchingNextPage && <div className="p-4 flex justify-center"><LoaderCircle className="animate-spin text-muted-foreground" size={24} /></div>}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 py-10">
                <LoaderCircle className="animate-spin w-6 h-6" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 py-10">
                <MessageCircle strokeWidth={1} className="w-12 h-12 opacity-20" />
                <p className="text-sm font-medium">No messages yet</p>
                <p className="text-xs opacity-70">Start the conversation by sending a message below.</p>
              </div>
            ) : (
              groupedMessages.map((day) => (
                <div key={day.date}>
                  <div className="text-center text-xs text-muted-foreground my-2">
                    {/* parseISO reads "2026-11-03" as local midnight; new Date() reads it as
                        UTC, which shows the previous day west of UTC (the US). */}
                    {format(parseISO(day.date), "dd MMM yyyy")}
                  </div>

                  {day.groups.map((group, idx) => (
                    <div key={idx} className="mb-2 flex flex-col gap-1">
                      {group.messages.map((msg, i) => (
                        // Identity, not position. With an index key, loading an
                        // older page shifts every index and React reuses the
                        // wrong DOM node for the wrong message.
                        <Fragment key={msg.id ?? msg.client_msg_id ?? i}>
                        {msg.id === firstUnreadId && (
                          <div className="flex items-center gap-3 my-3">
                            <div className="h-px flex-1 bg-[#883734]/30" />
                            <span className="text-[11px] font-medium text-[#883734] whitespace-nowrap">
                              {unreadLabel}
                            </span>
                            <div className="h-px flex-1 bg-[#883734]/30" />
                          </div>
                        )}
                        <div
                          className={cn(
                            "flex w-fit max-w-[85%] md:max-w-[75%] flex-col gap-2 rounded-lg px-3 py-2 text-sm min-w-0",
                            msg.sender_id === getUserId(user)
                              ? "ml-auto"
                              : ""
                          )}
                        >
                          {
                            msg.sender_id === getUserId(user) ? <div className="flex items-start gap-[4px] w-full justify-end min-w-0">
                              <div className="flex-1 min-w-0 flex flex-col items-end">
                                <p className="caption text-[#777777] !text-xs flex justify-end">{format(new Date(msg.created_at), 'dd MMM yyyy')} &#x2022;  {formatTimeInUserTimezone(msg.created_at)} </p>
                                <p className="font-semibold flex justify-end">{msg?.sender_name} </p>
                                <div className="mb-2">
                                  {msg?.media_url?.length > 0 && (
                                    <div
                                      className={cn(
                                        "mt-2 gap-3",
                                        msg?.media_url?.length === 1 ? "flex" : "grid grid-cols-2"
                                      )}
                                    >
                                      {msg?.media_url?.map((media) => (
                                        <MediaRenderer key={media.url} file={media} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {
                                  msg.message !== '' && (
                                    <div
                                      className={cn(
                                        "bg-[#5C6BC026] body2 p-[8px] rounded-[4px] leading-relaxed break-words [overflow-wrap:anywhere] [word-break:break-word] whitespace-pre-wrap [&_p]:min-h-[1rem] w-fit max-w-full overflow-hidden text-left",
                                        richTextClass,
                                        Number(msg.isAttention) === 1 && "border border-[#883734de]"
                                      )}
                                      dangerouslySetInnerHTML={{ __html: formatMessageHTML(msg.message) }}
                                    />
                                  )
                                }
                                {/*
                                  Delivery state, shown only on messages this
                                  client sent and the server has not confirmed.
                                  A failed send used to look identical to a
                                  delivered one until the page was reloaded and
                                  the message turned out never to have existed.
                                */}
                                {msg.status === 'sending' && (
                                  <p className="caption text-[#777777] !text-xs mt-0.5">Sending…</p>
                                )}
                                {/*
                                  Seen tick, own messages only, once the send is
                                  confirmed (a pending/failed row has no server
                                  is_read to show yet). `is_read` is computed
                                  server-side per viewer -- see the effect in
                                  useOrderChat that refetches it on the other
                                  side's chat_read.
                                */}
                                {!msg.status && (
                                  msg.is_read ? (
                                    <CheckCheck className="h-3.5 w-3.5 shrink-0 self-end text-green-500" />
                                  ) : (
                                    <Check className="h-3.5 w-3.5 shrink-0 self-end text-[#777777]" />
                                  )
                                )}
                                {msg.status === 'failed' && (
                                  <div className="caption !text-xs mt-0.5 text-red-600 flex items-center gap-1 justify-end flex-wrap">
                                    <span>{failureReason(msg.error_code)}</span>
                                    {onRetryMessage && (
                                      <Button
                                        type="button"
                                        variant="link"
                                        onClick={() => onRetryMessage(msg)}
                                        className="!h-auto !p-0 !text-xs text-red-600 underline underline-offset-2 hover:text-red-800"
                                      >
                                        Retry
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Avatar className='w-[30px] h-[30px] rounded-[4px] shrink-0'>
                                <AvatarImage src={msg?.sender_picture} alt={msg?.sender_name} className="w-full h-full rounded-[4px]" />
                                <AvatarFallback className='flex items-center justify-center bg-gray-500 w-full h-full text-white rounded-[4px]'>{msg?.sender_name?.[0] ?? 'U'}</AvatarFallback>
                              </Avatar>

                            </div> : <div className="flex items-start gap-[4px] w-full min-w-0">
                              <Avatar className='w-[30px] h-[30px] rounded-[4px] shrink-0'>
                                <AvatarImage src={msg?.sender_picture} alt={msg?.sender_name} className="w-full h-full rounded-[4px]" />
                                <AvatarFallback className='flex items-center justify-center bg-gray-500 w-full h-full text-white rounded-[4px]'>{msg?.sender_name?.[0] ?? 'U'}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="caption text-[#777777] !text-xs">{format(new Date(msg.created_at), 'dd MMM yyyy')} &#x2022; {formatTimeInUserTimezone(msg.created_at)} </p>
                                <p className="font-semibold">{msg?.sender_name} </p>
                                <div className="mb-2">
                                  {msg?.media_url?.length > 0 && (
                                    <div
                                      className={cn(
                                        "mt-2 gap-3",
                                        msg?.media_url?.length === 1 ? "flex" : "grid grid-cols-2"
                                      )}
                                    >
                                      {msg?.media_url?.map((media) => (
                                        <MediaRenderer key={media.url} file={media} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {
                                  msg.message !== '' && (
                                    <div
                                      className={cn(
                                        "bg-[#5C6BC026] body2 p-[8px] rounded-[4px] leading-relaxed break-words [overflow-wrap:anywhere] [word-break:break-word] whitespace-pre-wrap [&_p]:min-h-[1rem] w-fit max-w-full overflow-hidden text-left",
                                        richTextClass,
                                        Number(msg.isAttention) === 1 && "border border-[#883734de]"
                                      )}
                                      dangerouslySetInnerHTML={{ __html: formatMessageHTML(msg.message) }}
                                    />
                                  )
                                }

                              </div>
                            </div>
                          }

                        </div>
                        </Fragment>
                      ))}
                      <p className="flex justify-end">{
                        isMediaUploading && <p>Uploading...</p>
                      }</p>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      <div className="flex items-center p-6 pt-4 pb-4 flex-col gap-2 border-t border-[#DBDBDB]/50">
        {composerError && (
          <p className="w-full text-xs text-red-600">{composerError}</p>
        )}
        {pendingFiles.length > 0 &&
          <div className="flex gap-[8px] flex-wrap bg-[#f6f6f6] w-full p-2 rounded overflow-x-auto border">
            {pendingFiles.map((media, idx) => (
              <div key={media.url} className="relative group/media">
                <MediaRenderer file={media} className="!h-[52px] !w-[52px]" />
                <button
                  type="button"
                  onClick={() => removePendingFile(idx)}
                  className="absolute -top-2 -right-2 bg-red-500 rounded-full text-white w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover/media:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <div
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="w-[52px] h-[52px] rounded-[4px] border border-[#DBDBDB] flex items-center justify-center cursor-pointer hover:bg-[#f0f0f0] transition"
            >
              <Plus />
            </div>
          </div>
        }
        <form onSubmit={handleSubmit} className="w-full relative">
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileInputRef?.current?.click()}
              className="p-1.5 hover:bg-gray-100 rounded-full text-gray-500 transition mb-1 shrink-0"
            >
              <Paperclip className="cursor-pointer w-5 h-5" />
            </button>
            <div className="relative w-full flex-1 min-w-0">
              <RichTextEditor
                placeholder="Type your message..."
                value={input}
                onChange={setInput}
                onEnter={sendMessage}
                onPasteFiles={handleFiles}
                className="bg-transparent border border-[#DBDBDB]/50 rounded-[6px] shadow-[inset_0.38px_-0.38px_3.02px_0px_rgba(0,0,0,0.12)] focus-within:ring-2 focus-within:ring-ring/50 focus-within:border-transparent transition-all overflow-hidden"
                editorClass="min-h-[40px] max-h-[150px] overflow-y-auto overflow-x-hidden pl-[6px] pr-12 py-[3px] text-caption1 break-words [overflow-wrap:anywhere] whitespace-pre-wrap"
              />
              {
                isAdmin && <div className="absolute right-6 bottom-1.5 text-gray-500 z-10">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {isAttention ? (
                          <SquareCheck
                            onClick={handleAttentionToggle}
                            className="cursor-pointer w-5 h-5 text-gray-600 hover:text-black transition"
                          />
                        ) : (
                          <Square
                            onClick={handleAttentionToggle}
                            className="cursor-pointer w-5 h-5 text-gray-600 hover:text-black transition"
                          />
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{isAttention ? "Remove attention" : "Mark as attention"}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              }
            </div>

            <Button
              type="submit"
              size="icon"
              className="p-1 rounded-full shrink-0 mb-1"
              disabled={(inputLength === 0 && pendingFiles.length === 0) || isMediaUploading}
            >
              {isMediaUploading ? <LoaderCircle className="size-3.5 animate-spin" /> : <ArrowUpIcon className="size-3.5" />}
              <span className="sr-only">{isMediaUploading ? "Sending..." : "Send"}</span>
            </Button>
          </div>
        </form>
      </div>
    </div >
  )
}